import * as React from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Navigation from "./components/Navigation";
import TopBar from "./components/TopBar";
import PageErrorBoundary from "./components/PageErrorBoundary";
import { usePreload } from "../src/contexts/DataContext/PreloadContext";

const STORAGE_KEY = "am.sidebarCollapsed";

const readCollapsed = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const Layout = () => {
  const theme = useTheme();
  const { loading } = usePreload();
  const { pathname } = useLocation();
  // noSsr: evaluate on first render so desktop doesn't flash the mobile drawer
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"), { noSsr: true });
  const [collapsed, setCollapsed] = React.useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  React.useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(collapsed));
    } catch {
      // storage unavailable; the setting just won't persist
    }
  }, [collapsed]);

  // Close the mobile drawer on route change or when crossing the breakpoint
  React.useEffect(() => {
    setMobileOpen(false);
  }, [pathname, isDesktop]);

  const handleToggle = () =>
    isDesktop ? setCollapsed((c) => !c) : setMobileOpen((o) => !o);

  return (
    <Box display="flex" height="100vh" width="100%">
      <Navigation
        collapsed={collapsed}
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        isDesktop={isDesktop}
      />
      <Box flex={1} display="flex" flexDirection="column" minWidth={0}>
        <TopBar
          collapsed={collapsed}
          isDesktop={isDesktop}
          onToggle={handleToggle}
          loading={loading}
        />
        <Box
          component="main"
          flex={1}
          overflow="auto"
          bgcolor={theme.palette.shell.contentBg}
        >
          <PageErrorBoundary key={pathname}>
            <Outlet />
          </PageErrorBoundary>
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
